import Box from "@material-ui/core/esm/Box";
import Dialog from "@material-ui/core/esm/Dialog";
import DialogTitle from "@material-ui/core/esm/DialogTitle";
import DialogActions from "@material-ui/core/esm/DialogActions";
import DialogContent from "@material-ui/core/esm/DialogContent";
import TextField from "@material-ui/core/esm/TextField";
import FormControl from "@material-ui/core/esm/FormControl";
import RadioGroup from "@material-ui/core/esm/RadioGroup";
import FormControlLabel from "@material-ui/core/esm/FormControlLabel";
import Radio from "@material-ui/core/esm/Radio";
import CloseIcon from '@material-ui/icons/Close';
import axios from 'axios';
import { nanoid } from 'nanoid';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CustomerService } from 'api';
import { PrimaryButton, SecondaryButton } from 'components/Button';
import Divider from 'components/Divider/Divider';
import { Checkbox, FormElement } from 'components/Form';
import { Title } from 'components/Text';
import { BLACK } from 'constants/colors';
import useCurrentCustomer from 'hooks/customer/useCurrentCustomer';
import useGetValidationString from 'hooks/language/useGetValidationString';
import isValidEmail from 'utils/text/isValidEmail';
import useStyles from '../ProfileStyles';
import { EmailDestination } from 'modules/CreateCustomer/CreateCustomerBillingEmail/CreateCustomerBillingEmail';
const idSuffix = nanoid();
export default function BillingEmailDialog({
  open,
  onClose
}) {
  const {
    t
  } = useTranslation();
  const {
    customer,
    setCustomer
  } = useCurrentCustomer();
  const {
    dialog,
    dialogTitle,
    closeIcon,
    submitButton,
    textField
  } = useStyles();
  const {
    control
  } = useForm();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const {
    emailFormat
  } = useGetValidationString();
  const [isLoading, setIsLoading] = useState(false);
  const [isReceiveInvoicesByEmail, setIsReceiveInvoicesByEmail] = useState(customer?.receiveInvoicesByEmail ?? false);
  const [emailDestination, setEmailDestination] = useState(EmailDestination.SAME_AS_CUSTOMER_EMAIL);
  const [billingEmail, setBillingEmail] = useState(customer?.billingEmail ?? '');
  const handleRadioGroupChange = event => {
    setEmailDestination(event.target.value);
  };
  const submitEmail = async () => {
    if (customer) {
      try {
        setIsLoading(true);
        if (customer.id) {
          const customerBody = {
            ...customer,
            billingEmail: shouldUseCustomerEmailAsBilling() ? undefined : billingEmail,
            receiveInvoicesByEmail: isReceiveInvoicesByEmail
          };
          const customerObject = await CustomerService.editCustomer({
            customerId: customer.id,
            body: customerBody
          });
          setCustomer(customerObject);
        } else {
          console.error('Could not find customer id', customer.id);
        }
      } catch (e) {
        if (!axios.isCancel(e)) enqueueSnackbar(t('error.edit_billing_email'), {
          variant: 'error'
        });
      } finally {
        setIsLoading(false);
        onClose();
      }
    }
  };
  useEffect(() => {
    if (customer?.billingEmail !== customer?.email) {
      setEmailDestination(EmailDestination.SPECIFIC_BILLING_EMAIL);
    }
  }, []);
  const shouldUseCustomerEmailAsBilling = () => {
    return emailDestination === EmailDestination.SAME_AS_CUSTOMER_EMAIL || !isReceiveInvoicesByEmail;
  };
  return /*#__PURE__*/React.createElement(Dialog, {
    "aria-labelledby": "email-dialog-form",
    open: open,
    onClose: onClose
  }, /*#__PURE__*/React.createElement(DialogTitle, {
    id: "email-dialog-form"
  }, /*#__PURE__*/React.createElement("div", {
    className: dialogTitle
  }, /*#__PURE__*/React.createElement(Title, {
    component: "h1"
  }, t('email.edit_billing_email')), /*#__PURE__*/React.createElement(SecondaryButton, {
    className: closeIcon,
    ariaLabel: "close dialog",
    onClick: onClose
  }, /*#__PURE__*/React.createElement(CloseIcon, {
    htmlColor: BLACK
  })))), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement("form", null, /*#__PURE__*/React.createElement(DialogContent, {
    className: dialog
  }, /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "receiveInvoicesByEmail",
    defaultValue: isReceiveInvoicesByEmail,
    render: ({
      onChange,
      value,
      name
    }) => /*#__PURE__*/React.createElement(Checkbox, {
      name: name,
      setChecked: isChecked => {
        onChange(isChecked);
        setIsReceiveInvoicesByEmail(isChecked);
      },
      checked: value,
      label: t('email.email_invoice_distribution'),
      infoText: t('email.invoice_distribution_email_help_text')
    })
  }), isReceiveInvoicesByEmail && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormControl, null, /*#__PURE__*/React.createElement(Box, {
    my: 2
  }, /*#__PURE__*/React.createElement(RadioGroup, {
    "aria-label": "email destination",
    name: "emailDestination",
    value: emailDestination,
    onChange: handleRadioGroupChange
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    value: EmailDestination.SAME_AS_CUSTOMER_EMAIL,
    control: /*#__PURE__*/React.createElement(Radio, null),
    label: t('email.radio_same_as_customer_email')
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    value: EmailDestination.SPECIFIC_BILLING_EMAIL,
    control: /*#__PURE__*/React.createElement(Radio, null),
    label: t('email.radio_specific_billing_email')
  })))), emailDestination === EmailDestination.SPECIFIC_BILLING_EMAIL && /*#__PURE__*/React.createElement(FormElement, {
    title: t('email.billing_email')
  }, /*#__PURE__*/React.createElement("div", {
    className: textField
  }, /*#__PURE__*/React.createElement(TextField, {
    name: "billingEmail",
    className: textField,
    variant: "outlined",
    fullWidth: true,
    value: billingEmail,
    error: !isValidEmail(billingEmail),
    onChange: e => {
      console.log(e);
      setBillingEmail(e.target.value);
    },
    helperText: !isValidEmail(billingEmail) ? emailFormat : '',
    inputProps: {
      id: `email_${idSuffix}`
    },
    placeholder: t('profile.email')
  }))))), /*#__PURE__*/React.createElement(DialogActions, null, /*#__PURE__*/React.createElement("div", {
    className: submitButton
  }, /*#__PURE__*/React.createElement(PrimaryButton, {
    ariaLabel: "submit email",
    onClick: submitEmail,
    disabled: isReceiveInvoicesByEmail && emailDestination === EmailDestination.SPECIFIC_BILLING_EMAIL && !isValidEmail(billingEmail),
    loading: isLoading
  }, t('buttons.save_edits'))))));
}